import Promise from 'bluebird';
import _ from 'underscore';

import { constants as C } from '@bingads-webui-campaign-react/image-experience-model';

class ImageSizeValidationError extends Error {
  constructor({
    imageWidth,
    imageHeight,
    minWidth,
    minHeight,
  }) {
    super('SizeValidationError'); // if you change this, please update getMultiAddedImageErrorMessage

    this.imageWidthInternal = imageWidth;
    this.imageHeightInternal = imageHeight;
    this.minWidthInternal = minWidth;
    this.minHeightInternal = minHeight;
  }

  get imageWidth() {
    return this.imageWidthInternal;
  }

  get imageHeight() {
    return this.imageHeightInternal;
  }

  get minWidth() {
    return this.minWidthInternal;
  }

  get minHeight() {
    return this.minHeightInternal;
  }
}

class ImageLoadError extends Error {
  constructor() {
    super('ImageLoadError');
  }
}

const validateImageWithDimension = function (
  width,
  height,
  imageType,
  isMultimediaAd = false,
  imageOption = C.defaultImageOption,
  isMMAV2Enabled = false,
  isCombinedMsanAndMmaAspectRatios = false,
  isDisplayAds = false,
  isPortraitImage = false
) {
  const minimumSizeForImage =
    C.getMinimumSizeForImage(imageType, isMultimediaAd, imageOption, isCombinedMsanAndMmaAspectRatios, isMMAV2Enabled, isDisplayAds, isPortraitImage);

  if (width < minimumSizeForImage[0] ||
    height < minimumSizeForImage[1]) {
    return new ImageSizeValidationError({
      imageWidth: width,
      imageHeight: height,
      minWidth: minimumSizeForImage[0],
      minHeight: minimumSizeForImage[1],
    });
  }

  return null;
};

const validateImageSyncAsyncWithDimensionParams = ({
  imageSource,
  minimumHeight,
  minimumWidth,
}) => {
  const img = new Image();

  let imageLoadedResolve = null;
  let imageLoadedReject = null;
  const imageLoadedPromise = new Promise((resolve, reject) => {
    imageLoadedResolve = resolve;
    imageLoadedReject = reject;
  });

  img.src = imageSource;

  img.onload = () => {
    if (img.naturalWidth < minimumWidth ||
      img.naturalHeight < minimumHeight) {
      imageLoadedReject(new ImageSizeValidationError({
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
        minWidth: minimumWidth,
        minHeight: minimumHeight,
      }));
    } else {
      imageLoadedResolve();
    }
  };

  img.onerror = (err) => {
    imageLoadedReject(new ImageLoadError(err));
  };

  return imageLoadedPromise;
};

const validateImageSizeAsync = function (
  imageSource,
  imageType,
  isMultimediaAd = false,
  imageOption = C.defaultImageOption,
  isMMAV2Enabled = false,
  isCombinedMsanAndMmaAspectRatios = false,
  isDisplayAds = false,
  isPortraitImage = false
) {
  const minimumSize = C.getMinimumSizeForImage(imageType, isMultimediaAd, imageOption, isCombinedMsanAndMmaAspectRatios, isMMAV2Enabled, isDisplayAds, isPortraitImage);

  return validateImageSyncAsyncWithDimensionParams({
    imageSource,
    minimumHeight: minimumSize[1],
    minimumWidth: minimumSize[0],
  });
};

function getImageValidationErrorMessage(e, i18n, isMultiple = false) {
  if (e instanceof ImageSizeValidationError) {
    let stringTemplateKey = isMultiple ? 'ImageExperience_MultipleUpload_SizeValidationFailTemplate' : 'ImageExperience_SizeValidationFailTemplate';
    /* istanbul ignore else */
    if (e.isDueToResizing) {
      stringTemplateKey = 'ImageExperience_SizeValidationFailTemplate_WithDetail';
    }

    return i18n.getString(stringTemplateKey, {
      width: e.minWidth,
      height: e.minHeight,
    });
  }
  const error = _.result(e, 'message', '');

  return i18n.getString(`ImageExperience_Error_${error}`) || error;
}

function getMultiAddedImageErrorMessage(errors, i18n) {
  const uniqErrors = _.uniq(errors);
  const hasValidateError = _.some(uniqErrors, e => e.indexOf('SizeValidationError') > -1);
  const hasApiError = _.some(uniqErrors, e => e.indexOf('SizeValidationError') === -1);

  if (hasValidateError && hasApiError) {
    return i18n.getString('ImageExperience_Error_MixedError');
  } else if (hasValidateError) {
    return i18n.getString('ImageExperience_Error_ValidationError');
  } else if (hasApiError) {
    return i18n.getString('ImageExperience_Error_APIError');
  }

  return '';
}


const validateMultipleImagesSizesAsync = ({
  imageSources,
  minimumHeight,
  minimumWidth,
}) => {
  const validations = [];

  _.each(imageSources, (imageSource) => {
    validations.push(validateImageSyncAsyncWithDimensionParams({
      imageSource,
      minimumHeight,
      minimumWidth,
    }));
  });

  return Promise.all(validations);
};

export {
  ImageSizeValidationError, ImageLoadError,
  validateImageSizeAsync,
  validateImageWithDimension,
  validateImageSyncAsyncWithDimensionParams,
  validateMultipleImagesSizesAsync,
  getImageValidationErrorMessage,
  getMultiAddedImageErrorMessage,
};
